import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { CronNotificationSetting, ChannelConfigSummary } from '../../types/cron.js';
import * as cronApi from '../../services/cron-api.js';

@customElement('cp-cron-notification-settings')
export class CpCronNotificationSettings extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) jobId: number | null = null;

  @state() private _channels: ChannelConfigSummary[] = [];
  @state() private _enabled = false;
  @state() private _channelId: number | null = null;
  @state() private _onSuccess = 1;
  @state() private _onFailure = 1;
  @state() private _onStart = 0;
  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _existingSetting: CronNotificationSetting | null = null;

  updated(changed: Map<string, unknown>): void {
    if (changed.has('jobId') && this.jobId) {
      void this._load();
    }
  }

  private async _load(): Promise<void> {
    this._loading = true;
    try {
      const [channels, settings] = await Promise.all([
        cronApi.getChannels(),
        this.jobId ? cronApi.getNotificationSettings(this.jobId) : Promise.resolve([]),
      ]);
      this._channels = channels;

      if (settings.length > 0) {
        const s = settings[0];
        this._existingSetting = s;
        this._enabled = true;
        this._channelId = s.channel_config_id;
        this._onSuccess = s.on_success;
        this._onFailure = s.on_failure;
        this._onStart = s.on_start;
      } else {
        this._existingSetting = null;
        this._enabled = false;
        this._channelId = channels.length > 0 ? channels[0].id : null;
        this._onSuccess = 1;
        this._onFailure = 1;
        this._onStart = 0;
      }
    } catch {
      this._channels = [];
    } finally {
      this._loading = false;
    }
  }

  async save(): Promise<void> {
    if (!this.jobId) return;

    this._saving = true;
    try {
      if (this._enabled && this._channelId) {
        await cronApi.saveNotificationSettings(this.jobId, {
          channel_config_id: this._channelId,
          on_success: this._onSuccess,
          on_failure: this._onFailure,
          on_start: this._onStart,
        });
      } else if (!this._enabled && this._existingSetting) {
        await cronApi.deleteNotificationSettings(this.jobId);
      }
    } finally {
      this._saving = false;
    }
  }

  private _onEnabledChange(e: Event): void {
    this._enabled = (e.target as HTMLInputElement).checked;
  }

  private _onChannelChange(e: Event): void {
    const val = (e.target as HTMLSelectElement).value;
    this._channelId = val ? parseInt(val, 10) : null;
  }

  private _onSuccessChange(e: Event): void {
    this._onSuccess = (e.target as HTMLInputElement).checked ? 1 : 0;
  }

  private _onFailureChange(e: Event): void {
    this._onFailure = (e.target as HTMLInputElement).checked ? 1 : 0;
  }

  private _onStartChange(e: Event): void {
    this._onStart = (e.target as HTMLInputElement).checked ? 1 : 0;
  }

  render() {
    if (this._loading) {
      return html`<div class="cp-cron-notification__loading">Lade Channels...</div>`;
    }

    const noChannels = this._channels.length === 0;

    return html`
      <div class="cp-cron-notification">
        <div class="cp-cron-notification__header">
          <label class="cp-cron-notification__toggle">
            <input
              type="checkbox"
              .checked=${this._enabled}
              ?disabled=${noChannels}
              @change=${this._onEnabledChange}
            />
            <span class="cp-cron-notification__toggle-label">Benachrichtigungen</span>
          </label>
        </div>

        ${noChannels ? html`
          <div class="cp-cron-notification__hint">
            Bitte zuerst einen Channel in den Settings konfigurieren
          </div>
        ` : nothing}

        ${this._enabled && !noChannels ? html`
          <div class="cp-cron-notification__settings">
            <div class="cp-cron-notification__field">
              <label class="cp-cron-notification__label">Channel</label>
              <select
                class="cp-cron-notification__select"
                @change=${this._onChannelChange}
              >
                ${this._channels.map(ch => html`
                  <option value=${String(ch.id)} ?selected=${this._channelId === ch.id}>
                    ${ch.display_name} (${ch.channel_type})
                  </option>
                `)}
              </select>
            </div>

            <div class="cp-cron-notification__field">
              <label class="cp-cron-notification__label">Benachrichtigen bei</label>
              <div class="cp-cron-notification__checkboxes">
                <label class="cp-cron-notification__checkbox">
                  <input type="checkbox" .checked=${!!this._onSuccess} @change=${this._onSuccessChange} />
                  <span>Erfolg</span>
                </label>
                <label class="cp-cron-notification__checkbox">
                  <input type="checkbox" .checked=${!!this._onFailure} @change=${this._onFailureChange} />
                  <span>Fehler</span>
                </label>
                <label class="cp-cron-notification__checkbox">
                  <input type="checkbox" .checked=${!!this._onStart} @change=${this._onStartChange} />
                  <span>Start</span>
                </label>
              </div>
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-cron-notification-settings': CpCronNotificationSettings;
  }
}
